import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Bell,
  Info,
  LogOut,
  Menu,
  X,
  Settings,
  BarChart2
} from 'lucide-react';
import { api } from '../services/api';
import logo from '../assets/Logo.png';
import { NotificationSettings } from './NotificationSettings';

export const Navbar = ({ isAuthenticated, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showVersion, setShowVersion] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  // Add these to your component's state
  const appVersion = "v1.1.2";
  const buildNumber = "31";
  const copyright = "© 2026 Moribula Investments Solutions, Inc. All rights reserved.";

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Home },
    { name: 'Events', path: '/events', icon: Bell },
    { name: 'Reporting', path: '/reports', icon: BarChart2 },
    { name: 'About', path: '/about', icon: Info, onClick: () => setShowVersion(!showVersion) },
  ];

  const handleLogout = async () => {
    const username = localStorage.getItem('username');
    try {
      // Use the API service logout method
      await api.logout(username);
    } catch (error) {
      console.error('Error recording logout:', error);
    } finally {
      // Proceed with logout regardless of recording success
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  
  return isAuthenticated ? (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Company Name */}
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center">
              <img
                src={logo}
                alt="Moribula"
                className="h-8 w-auto mr-3"
              />
              <span className="text-xl font-semibold text-gray-900">
               Net Runner Platform
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-4">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.name}
                    className="relative"
                  >
                    {item.name === 'About' ? (
                      <button
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                          isActivePath(item.path)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={item.onClick}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.name}
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                          isActivePath(item.path)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {item.name}
                      </Link>
                    )}
                    {item.name === 'About' && showVersion && (
                      // Then in your version popup
                      <div className="absolute top-10 left-0 mt-1 p-3 bg-white rounded shadow-md border border-gray-200 z-10 w-64">
                        <p className="text-lg font-bold text-gray-900">Net Runner Platform</p>
                        <p className="text-sm text-gray-700">Version {appVersion} (Build {buildNumber})</p>
                        <p className="text-xs mt-1 text-gray-500">{copyright}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right side actions */}
          <div className="flex items-center">
            <div className="hidden md:flex items-center">
              <button
                onClick={() => setShowNotificationSettings(true)}
                className="inline-flex items-center px-3 py-2 mr-4 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                title="Notification Settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 mr-4">
              {localStorage.getItem('username') || 'User'}
              </span>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="pt-2 pb-3 space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.name}>
                    {item.name === 'About' ? (
                      <button
                        className={`flex w-full items-center px-3 py-2 rounded-md text-base font-medium ${
                          isActivePath(item.path)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={item.onClick}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                          isActivePath(item.path)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </Link>
                    )}
                    {item.name === 'About' && showVersion && (
                      <div className="ml-8 mt-1 p-2 bg-gray-50 rounded text-sm">
                        <p className="font-medium text-gray-900">Application Version</p>
                        <p className="font-bold text-blue-600">{appVersion}</p>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="px-3 py-2 space-y-2">
                <button
                  onClick={() => setShowNotificationSettings(true)}
                  className="flex w-full items-center px-4 py-2 text-base font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                >
                  <Settings className="h-5 w-5 mr-2" />
                  Notification Settings
                </button>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notification Settings Modal */}
      <NotificationSettings
        show={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
      />
    </nav>
  ) : null;
};